import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Popover,
  Slide,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactNode, useContext, useEffect } from "react";
import { useState } from "react";
import "./TableTopBar.css";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import SettingsIcon from "@mui/icons-material/Settings";
import { BugReport, Feedback, Pause, PlayArrow } from "@mui/icons-material";
import IngameReportForm from "src/components/gametable/IngameReportForm";
import { useParams } from "react-router-dom";
import { UserContext } from "src/components/userContext";

type TableTopBarProps = {
  settingsElement: ReactNode;
  replayElement: ReactNode;
  playerSelectorElement: ReactNode;
  ruleLinksElement: ReactNode;
  chatOpen: boolean;
  toggleChat: () => void;
  logOpen: boolean;
  toggleLog: () => void;
  statusBarElement: ReactNode;
  tableStatus: string;
  chatElement: ReactNode;
  logElement: ReactNode;
  cgoTopBarAsButton: ReactNode;
  globalMessage: any;
  currEventNumber: number;
  deckContentSlotRef?: React.RefObject<HTMLDivElement>;
};

function TableTopBar({
  settingsElement,
  replayElement,
  playerSelectorElement,
  ruleLinksElement,
  chatOpen,
  toggleChat,
  logOpen,
  toggleLog,
  statusBarElement,
  tableStatus,
  chatElement,
  logElement,
  cgoTopBarAsButton,
  globalMessage,
  currEventNumber,
  deckContentSlotRef,
}: TableTopBarProps) {
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [replayControlsVisible, setReplayControlsVisible] = useState<boolean>(false);
  const [ingameFeedbackVisible, setIngameFeedbackVisible] = useState<boolean>(false);

  useEffect(() => {
    if (tableStatus !== "ended") {
      setReplayControlsVisible(false);
    }
  }, [tableStatus]);

  const settingsOpen = Boolean(settingsAnchorEl);
  const params = useParams();
  const userContext = useContext(UserContext);

  function toggleReplayControls() {
    setReplayControlsVisible(!replayControlsVisible);
  }

  const commonButtonSx = {
    "&.MuiButton-outlined": {
      backgroundColor: "white",
      ":hover": {
        backgroundColor: "white",
        boxShadow: 6,
      },
    },
  };

  const settingsButtonEl = (
    <Button onClick={(e) => setSettingsAnchorEl(e.currentTarget)} variant="outlined" size="small" sx={commonButtonSx}>
      <TableRestaurantIcon />
      <SettingsIcon />
    </Button>
  );

  const replayControlsButtonEl = (
    <Button
      onClick={() => toggleReplayControls()}
      variant="outlined"
      size="small"
      disabled={tableStatus !== "ended"}
      sx={commonButtonSx}
    >
      <PlayArrow />
      <Pause />
    </Button>
  );

  const ingameFeedbackButtonEl = (
    <Button
      variant={ingameFeedbackVisible ? "contained" : "outlined"}
      size="small"
      sx={commonButtonSx}
      onClick={() => setIngameFeedbackVisible((prev) => !prev)}
    >
      <Feedback />
      <BugReport />
    </Button>
  );

  const ingameFeedbackEl = (
    <IngameReportForm
      projectSlug={params.projectSlug ?? ""}
      currEventNumber={currEventNumber}
      tableId={params.tableId}
      visible={ingameFeedbackVisible}
    />
  );

  return (
    <div className="table-top-bar-wrapper">
      <div className={"table-top-bar-replay-bar" + (replayControlsVisible ? " visible" : " collapsed")}>
        {replayElement}
      </div>

      {globalMessage && (
        <Slide direction="down" in={true} mountOnEnter unmountOnExit>
          <Box
            sx={{
              //position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              backgroundColor: globalMessage.style?.backgroundColor || "black",
              color: globalMessage.style?.color || "white",
              padding: "10px",
              textAlign: "center",
              zIndex: 1000,
            }}
          >
            {globalMessage.message}
          </Box>
        </Slide>
      )}

      <div className="table-top-bar">
        <div className="table-top-bar-left">
          {settingsButtonEl}
          {tableStatus == "ended" ? replayControlsButtonEl : null}
          {userContext?.hasPermission("see-ingame-feedback") ? ingameFeedbackButtonEl : null}
        </div>

        <Popover
          open={settingsOpen}
          onClose={() => setSettingsAnchorEl(null)}
          anchorEl={settingsAnchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Stack gap={1}>
            <Paper elevation={3} sx={{ margin: "5px" }}>
              {playerSelectorElement}
            </Paper>

            <Paper elevation={3} sx={{ margin: "5px" }}>
              <Typography variant="h6" sx={{ marginLeft: "5px" }}>
                Table settings
              </Typography>
              {settingsElement}
            </Paper>

            <Paper elevation={3} sx={{ margin: "5px" }}>
              <Typography variant="h6" sx={{ marginLeft: "5px" }}>
                Rulebook links
              </Typography>
              {ruleLinksElement}
            </Paper>
          </Stack>
        </Popover>

        {ingameFeedbackEl}

        {statusBarElement}

        <div className="table-top-bar-right">
          <FormControlLabel
            control={
              <Tooltip title="Hotkey 'Enter'">
                <Switch checked={chatOpen} onChange={toggleChat} />
              </Tooltip>
            }
            label={"Chat"}
            labelPlacement="start"
            sx={{ justifyContent: "space-between" }}
          />
          <FormControlLabel
            control={
              <Tooltip title="Hotkey 'L'">
                <Switch checked={logOpen} onChange={toggleLog} />
              </Tooltip>
            }
            label={"Log"}
            labelPlacement="start"
            sx={{ justifyContent: "space-between" }}
          />
          {cgoTopBarAsButton}
        </div>
      </div>
      <div className={"chat-log-container" + (chatOpen && logOpen ? " both-open" : "")}>
        {chatElement}
        {logElement}
      </div>
      <div className="deck-content-slot" ref={deckContentSlotRef}></div>
    </div>
  );
}

export default TableTopBar;
